import { Fragment } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import GlobalFooter from "@/patterns/global-footer-v2/src";
import { ConfluencePageBanner } from "@/templates/confluence-software-page/src/sections/banner";
import { withFeatureExposed } from "@/utils/feature-exposed";

import {
    AtlSiteType,
    useLogMeIn
} from "../../../components/confluence/hooks/variant-cfind-1109/use-log-me-in";
import { ConfluenceSoftwarePageProps } from "../../../pages/confluence/confluence-software-page";

import { LeftLines } from "./graphics/left-lines";
import { RightLines } from "./graphics/right-lines";
import CtaSection from "./sections/cta";
import FeaturesSection from "./sections/features";
import HeroSection from "./sections/hero";
import IntegrationsSection from "./sections/integrations";
import ResourcesSection from "./sections/resources";
import SocialProofShort from "./sections/social-proof-short";
import TemplatesSection from "./sections/templates";
import TestimonialsSection from "./sections/testimonials";
import LogMeInSection from "./sections/variant-cfind-1109/log-me-in";
import ConfluenceSoftwareNav from "./sections/variant-cfind-1234/confluence-navbar";
import VideoSection from "./sections/video";
import WhyConfluenceSection from "./sections/why-confluence";

const backgroundStyle = css({
    background:
        "linear-gradient(180deg, #b3d4ff 0%, rgba(179, 212, 255, 0) 100%)",
    overflow: "visible",
    position: "absolute",
    insetBlockStart: 0,
    insetInlineStart: 0,
    width: "100%",
    height: "100%",
    [media.only.xxs]: {
        maxHeight: "1045px"
    },
    [media.only.xs]: {
        maxHeight: "1160px"
    },
    [media.only.sm]: {
        maxHeight: "1160px"
    },
    [media.above.md]: {
        maxHeight: "965px"
    }
});

const backgroundStyleLogMeIn = css({
    background:
        "linear-gradient(180deg, #b3d4ff 0%, rgba(179, 212, 255, 0) 100%)",
    overflow: "visible",
    position: "absolute",
    insetBlockStart: 0,
    insetInlineStart: 0,
    height: "100%",
    width: "100%",
    [media.only.xxs]: {
        maxHeight: "1045px"
    },
    [media.only.xs]: {
        maxHeight: "1160px"
    },
    [media.only.sm]: {
        maxHeight: "1160px"
    },
    [media.above.md]: {
        maxHeight: "965px"
    }
});

const leftLines = css({
    position: "absolute",
    insetBlockStart: 182,

    [media.below.sm]: {
        display: "none"
    },

    [media.only.sm]: {
        insetBlockStart: 200
    }
});

const leftLinesLogMeIn = css({
    position: "absolute",
    insetBlockStart: 210,

    [media.below.md]: {
        display: "none"
    }
});

const rightLines = css({
    position: "absolute",
    insetBlockStart: 672,
    insetInlineEnd: 0,

    [media.below.sm]: {
        display: "none"
    },

    [media.only.sm]: {
        insetBlockStart: 645
    }
});

const rightLinesLogMeIn = css({
    position: "absolute",
    insetBlockStart: 324,
    insetInlineEnd: 0,

    [media.below.md]: {
        display: "none"
    }
});

const relative = css({
    position: "relative"
});

const ConfluenceSoftwarePageComponent = ({
    availableProducts,
    userProfile
}: ConfluenceSoftwarePageProps) => {
    const { userProfileName, recentSites, shouldShowLogMeIn, loading } =
        useLogMeIn({
            availableProducts,
            userProfile,
            siteType: AtlSiteType.CONFLUENCE
        });

    return (
        <Fragment>
            <UFOSegment name="confluence-landing-confluenceSoftwareNav">
                <ConfluenceSoftwareNav />
            </UFOSegment>
            <UFOSegment name="confluence-landing-confluencePageBanner">
                <ConfluencePageBanner />
            </UFOSegment>
            {shouldShowLogMeIn ? (
                <div css={relative}>
                    <div css={backgroundStyleLogMeIn} />
                    <div css={leftLinesLogMeIn}>
                        <LeftLines />
                    </div>
                    <div css={rightLinesLogMeIn}>
                        <RightLines />
                    </div>
                    <div css={relative}>
                        <UFOSegment name="confluence-landing-logMeInSection">
                            <LogMeInSection
                                userProfileName={userProfileName}
                                confluenceSites={recentSites}
                                loading={loading}
                            />
                        </UFOSegment>
                        <UFOSegment name="confluence-landing-featuresSection">
                            <FeaturesSection />
                        </UFOSegment>
                        <UFOSegment name="confluence-landing-socialProofShort">
                            <SocialProofShort />
                        </UFOSegment>

                        <UFOSegment name="confluence-landing-videoSection">
                            <VideoSection />
                        </UFOSegment>
                    </div>
                </div>
            ) : (
                <div css={relative}>
                    <div css={backgroundStyle} />
                    <div css={leftLines}>
                        <LeftLines />
                    </div>
                    <div css={rightLines}>
                        <RightLines />
                    </div>
                    <div css={relative}>
                        <UFOSegment name="confluence-landing-heroSection">
                            <HeroSection />
                        </UFOSegment>
                        <UFOSegment name="confluence-landing-featuresSection">
                            <FeaturesSection />
                        </UFOSegment>
                        <UFOSegment name="confluence-landing-socialProofShort">
                            <SocialProofShort />
                        </UFOSegment>

                        <UFOSegment name="confluence-landing-videoSection">
                            <VideoSection />
                        </UFOSegment>
                    </div>
                </div>
            )}
            <div css={relative}>
                <UFOSegment name="confluence-landing-templatesSection">
                    <TemplatesSection />
                </UFOSegment>
                <UFOSegment name="confluence-landing-whyConfluenceSection">
                    <WhyConfluenceSection />
                </UFOSegment>
                <UFOSegment name="confluence-landing-integrationSection">
                    <IntegrationsSection />
                </UFOSegment>
                <UFOSegment name="confluence-landing-testimonialsSection">
                    <TestimonialsSection />
                </UFOSegment>
                <UFOSegment name="confluence-landing-resourcesSection">
                    <ResourcesSection />
                </UFOSegment>
                <UFOSegment name="confluence-landing-ctaSection">
                    <CtaSection />
                </UFOSegment>
            </div>
            <UFOSegment name="confluence-landing-globalFooter">
                <GlobalFooter />
            </UFOSegment>
        </Fragment>
    );
};

export const ConfluenceSoftwarePage = withFeatureExposed(
    "cfind-1106_confluence_product_tour_refresh",
    "Test",
    ConfluenceSoftwarePageComponent
);
